import type { RegionTypeDefinition } from "@aec-craft/nb-generative-core";
import type { Layout } from "@prisma/client-generated";
import { type ZodType, z } from "zod";
import { zBimObjectJson } from "./z-bim-object";
import { zChat } from "./z-chat";
import { zExport } from "./z-export";
import { zMassingPreview } from "./z-massing-preview";
import { zPipeSettings, zTemplateLoadType } from "./z-pipe-settings";

const zApartmentTemplate = z.object({
	id: z.string(),
	loadType: zTemplateLoadType,
	template: z.object({
		root: z.string(),
		record: z.record(z.string(), zBimObjectJson),
	}),
});

const zApartmentGroup = z.object({
	id: z.string(),
	name: z.string(),
	color: z.string(),
	deleted: z.boolean(),
	mixRatio: z.number(),
	mixLocked: z.boolean(),
	apartmentTemplates: z.array(zApartmentTemplate),
});

const zApartmentSet = z.object({
	id: z.string(),
	name: z.string(),
	corridorWidth: z.number(),
	apartmentGroups: z.array(zApartmentGroup),
});

export type ApartmentSet = z.infer<typeof zApartmentSet>;

const hexColorRegex = /^#[\da-f]{6}$/i;

export type LayoutRegionType = RegionTypeDefinition & { color: string };
export const zLayoutRegionType = z.object({
	id: z.string(),
	label: z.string().trim().min(1).max(255),
	color: z.string().regex(hexColorRegex),
}) satisfies ZodType<LayoutRegionType>;

export const zLayout = z.object({
	projectId: z.string().uuid(),
	id: z.string().uuid(),
	name: z
		.string()
		.min(1, "Project name is required")
		.max(255, "Project name must be less than 255 characters"),
	deleted: z.boolean(),
	defaultBuildingYear: z.number().int(),
	pipeSettings: zPipeSettings,
	bimObjects: z.array(zBimObjectJson),
	massingPreview: zMassingPreview,
	// Metrics
	layoutEfficiency: z.number(),
	gsi: z.number(),
	buildingFootprint: z.number(),
	gfa: z.number(),
	far: z.number(),
	avr: z.number(),
	// Valuation: Construction Costs (Base - per sqm rates)
	costElectricalPerSqm: z.number(),
	costHvacPerSqm: z.number(),
	costShellConstructionPerSqm: z.number(),
	costSiteSetupPerSqm: z.number(),
	costDrainagePerSqm: z.number(),
	costFacadePerSqm: z.number(),
	// Valuation: Construction Costs (Unit-based rates)
	costAncillaryPerSqm: z.number(),
	costLiftPerFloor: z.number(),
	costDoorsPerApp: z.number(),
	costBathroomsPerApp: z.number(),
	costBalconyPerApp: z.number(),
	costParkingPerApp: z.number(),
	costRoofPerSqm: z.number(),
	costCellarPerSqm: z.number(),
	// Valuation: Project Costs
	landCostPerSqm: z.number(),
	sideCostsPercent: z.number(),
	// Valuation: Financing
	buildTimeYears: z.number(),
	equitySharePercent: z.number(),
	loanInterestRatePercent: z.number(),
	// Valuation: Revenue
	salesPricePerSqm: z.number(),
	salesPriceOverride: z.boolean(),
}) satisfies ZodType<Layout>;

export const zGetLayoutReq = z.object({
	layoutId: z.string().uuid(),
});

export const zGetLayoutRes = zLayout
	.pick({
		id: true,
		name: true,
		massingPreview: true,
		defaultBuildingYear: true,
	})
	.extend({
		pipeSettings: zPipeSettings.pick({
			id: true,
			massingSeed: true,
			minWingLength: true,
			maxWingLength: true,
			wingMinSetback: true,
			wingSetbackFactor: true,
			corridorWidth: true,
			unitHeight: true,
			unitDepth: true,
			maxLevels: true,
			commercialHeight: true,
			maxGsi: true,
			maxAncillaryGsi: true,
			targetFar: true,
			parkingMultiplier: true,
			parkingFactors: true,
			facadeWallThickness: true,
			partitionWallThickness: true,
			structuralFloorThickness: true,
			finishingFloorThickness: true,
			roofThickness: true,
			maxFragmentLength: true,
			maxCornerFragmentLength: true,
			maxCornerFragmentLengthOn: true,
			maxGsiOn: true,
			maxAncillaryGsiOn: true,
			targetFarOn: true,
			balconiesAffectMassing: true,
			corridorEndApartments: true,
			shiftedCorners: true,
			fragmentGapFillMethod: true,
			resizeCornerCores: true,
			avoidNorthFactor: true,
			unitMixOn: true,
			loadType: true,
			apartmentSetId: true,
		}),
		chats: z.array(zChat.omit({ layoutId: true })),
		apartmentSets: z.array(zApartmentSet),
		exports: z.array(zExport),
		regionTypes: z.array(zLayoutRegionType),
	});
